<script setup>
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ narratives: [], generatedAt: null }),
  },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['refresh']);

const KIND_META = {
  positive_dominant: { label: 'Positiva dominante', stripe: 'bg-emerald-500', text: 'text-emerald-700', icon: 'i-lucide-trending-up', bar: 'bg-emerald-500' },
  negative_dominant: { label: 'Negativa dominante', stripe: 'bg-rose-500', text: 'text-rose-700', icon: 'i-lucide-trending-down', bar: 'bg-rose-500' },
  rising: { label: 'Em ascensão', stripe: 'bg-amber-500', text: 'text-amber-700', icon: 'i-lucide-flame', bar: 'bg-amber-500' },
  coordinated_attack: { label: 'Ataque coordenado', stripe: 'bg-rose-700', text: 'text-rose-800', icon: 'i-lucide-shield-alert', bar: 'bg-rose-700' },
  repositioning_opportunity: { label: 'Reposicionamento', stripe: 'bg-blue-500', text: 'text-blue-700', icon: 'i-lucide-lightbulb', bar: 'bg-blue-500' },
};

const groupedByKind = computed(() => {
  const order = ['positive_dominant', 'negative_dominant', 'rising', 'coordinated_attack', 'repositioning_opportunity'];
  const map = {};
  (props.data.narratives || []).forEach(n => {
    map[n.kind] ||= [];
    map[n.kind].push(n);
  });
  return order
    .filter(k => map[k]?.length)
    .map(k => ({ kind: k, items: map[k], meta: KIND_META[k] }));
});

const expanded = ref(new Set());
const toggle = id => {
  if (expanded.value.has(id)) expanded.value.delete(id);
  else expanded.value.add(id);
  expanded.value = new Set(expanded.value);
};
const isOpen = id => expanded.value.has(id);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          Narrativas em Circulação
        </h3>
        <p class="mt-0.5 text-[11px] text-n-slate-11">
          O que o público está construindo sobre o perfil agora.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="loading"
        :disabled="disabled"
        @click="emit('refresh')"
      >
        Atualizar
      </Button>
    </div>

    <div v-if="disabled" class="py-6 text-center text-sm text-n-slate-11">
      Selecione um perfil para extrair narrativas.
    </div>

    <div v-else-if="loading && !data.narratives?.length" class="flex justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="!data.narratives?.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      Clique em "Atualizar" para extrair narrativas do período.
    </div>

    <div v-else class="space-y-4">
      <div v-for="group in groupedByKind" :key="group.kind">
        <div class="mb-2 flex items-center gap-2">
          <span :class="group.meta?.icon" class="size-4" />
          <h4 class="text-xs font-bold uppercase tracking-wide" :class="group.meta?.text">
            {{ group.meta?.label }}
          </h4>
        </div>

        <div class="space-y-2">
          <div
            v-for="n in group.items"
            :key="n.id || n.label"
            class="relative overflow-hidden rounded-lg border border-n-strong/40 bg-n-background p-3 pl-4 shadow-sm"
          >
            <span class="absolute inset-y-0 left-0 w-1" :class="group.meta?.stripe" />
            <div class="mb-2 flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <p class="text-sm font-bold text-n-slate-12">{{ n.label }}</p>
                <p v-if="n.description" class="mt-0.5 text-xs text-n-slate-11">{{ n.description }}</p>
              </div>
              <span class="shrink-0 rounded-md bg-n-slate-3 px-2 py-0.5 text-[10px] font-bold" :class="group.meta?.text">
                {{ n.intensity }}/100
              </span>
            </div>

            <div class="mb-2 h-1.5 overflow-hidden rounded-full bg-n-slate-3">
              <div class="h-full rounded-full" :class="group.meta?.bar" :style="{ width: `${n.intensity}%` }" />
            </div>

            <div v-if="n.counterNarrative" class="mb-2 flex gap-2 rounded-md border border-blue-200 bg-blue-50/70 p-2 text-[11px] text-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              <span class="i-lucide-corner-down-right size-3.5 shrink-0 text-blue-600" />
              <p><span class="font-semibold">Contra-narrativa: </span>{{ n.counterNarrative }}</p>
            </div>

            <button
              v-if="n.evidence?.length"
              type="button"
              class="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11 hover:text-n-slate-12"
              @click="toggle(n.id || n.label)"
            >
              <span :class="isOpen(n.id || n.label) ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" class="size-3" />
              {{ isOpen(n.id || n.label) ? 'Ocultar' : 'Ver' }} {{ n.evidence.length }} evidências
            </button>

            <div v-if="isOpen(n.id || n.label) && n.evidence?.length" class="mt-2 space-y-1.5">
              <div
                v-for="(ev, j) in n.evidence"
                :key="`ev-${n.id || n.label}-${j}`"
                class="rounded-md border border-n-weak bg-n-slate-2 p-2 text-[11px]"
              >
                <div class="mb-1 flex items-center justify-between gap-2">
                  <span class="font-semibold text-n-slate-12">{{ ev.authorName }}</span>
                  <span v-if="ev.likes" class="text-[9px] text-n-slate-11">♥ {{ ev.likes }}</span>
                </div>
                <p class="leading-relaxed text-n-slate-12">{{ ev.content }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
